import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  CreditCard, DollarSign, ArrowLeft, Plus, Trash2, Settings,
  CheckCircle, AlertCircle, ExternalLink, RefreshCw, Ticket,
  Calendar, Users, TrendingUp, Clock, Receipt, Ban,
  Building, Wallet, Percent, Globe, ShieldCheck, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Processor icons and colors
const processorStyles = {
  stripe: { icon: '💳', color: '#635BFF', bgColor: 'bg-indigo-500' },
  paypal: { icon: '🅿️', color: '#003087', bgColor: 'bg-blue-700' },
  square: { icon: '⬛', color: '#006AFF', bgColor: 'bg-blue-500' },
  venmo: { icon: '💵', color: '#008CFF', bgColor: 'bg-cyan-500' },
  cashapp: { icon: '💰', color: '#00D632', bgColor: 'bg-green-500' }
};

const LocalePayments = ({ user }) => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [processors, setProcessors] = useState({});
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Connect modal
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [connectForm, setConnectForm] = useState({
    api_key: '',
    secret_key: '',
    merchant_id: '',
    account_email: '',
    sandbox_mode: true
  });
  const [connecting, setConnecting] = useState(false);
  
  // Create ticket modal
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [userLocales, setUserLocales] = useState([]);
  const [ticketForm, setTicketForm] = useState({
    locale_id: '',
    ticket_name: '',
    price: '',
    quantity_available: 100,
    description: '',
    max_per_order: 10
  });
  
  useEffect(() => {
    fetchData();
    fetchUserLocales();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [processorsRes, connectionsRes, statsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/locale/payments/processors`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/payments/connections`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/payments/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/locale/payments/transactions`, { withCredentials: true })
      ]);
      
      setProcessors(processorsRes.data.processors || {});
      setConnections(connectionsRes.data.connections || []);
      setStats(statsRes.data);
      setTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      if (error.response?.status === 403) {
        toast.error('Business subscription required for payment features');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUserLocales = async () => {
    try {
      const response = await axios.get(`${API_URL}/locale/`, { withCredentials: true });
      // Filter to only show user's own locale posts
      const myLocales = (response.data.posts || []).filter(p => p.user_id === user?.user_id);
      setUserLocales(myLocales);
    } catch (error) {
      console.error('Failed to fetch user locales');
    }
  };
  
  const handleConnect = async () => {
    if (!selectedProcessor) return;
    
    setConnecting(true);
    try {
      const response = await axios.post(`${API_URL}/locale/payments/connect`, {
        processor: selectedProcessor,
        ...connectForm
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setShowConnectModal(false);
      setConnectForm({ api_key: '', secret_key: '', merchant_id: '', account_email: '', sandbox_mode: true });
      setSelectedProcessor(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect processor');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this payment processor?')) return;
    
    try {
      await axios.delete(`${API_URL}/locale/payments/connections/${connectionId}`, { withCredentials: true });
      toast.success('Processor disconnected');
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect processor');
    }
  };
  
  const handleToggleSandbox = async (connectionId, currentMode) => {
    try {
      await axios.put(
        `${API_URL}/locale/payments/connections/${connectionId}/toggle?sandbox_mode=${!currentMode}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Switched to ${!currentMode ? 'sandbox' : 'live'} mode`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle mode');
    }
  };
  
  const handleCreateTicket = async () => {
    if (!ticketForm.locale_id || !ticketForm.ticket_name || !ticketForm.price) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/locale/payments/tickets`, {
        ...ticketForm,
        price: parseFloat(ticketForm.price),
        quantity_available: parseInt(ticketForm.quantity_available),
        max_per_order: parseInt(ticketForm.max_per_order)
      }, { withCredentials: true });
      
      toast.success('Ticket created successfully!');
      setShowTicketModal(false);
      setTicketForm({ locale_id: '', ticket_name: '', price: '', quantity_available: 100, description: '', max_per_order: 10 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };
  
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/locale')}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                data-testid="back-to-locale"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold dark:text-white flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-primary" />
                  Payment Portal
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage payments for your Locale events & reservations
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowConnectModal(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="connect-processor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Connect Processor
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'processors', label: 'Processors', icon: CreditCard },
              { id: 'tickets', label: 'Tickets', icon: Ticket },
              { id: 'transactions', label: 'Transactions', icon: Receipt }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Total Revenue</p>
                    <p className="text-2xl font-bold" data-testid="total-revenue">
                      {formatCurrency(stats?.summary?.total_revenue || 0)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 opacity-50" />
                </div>
              </Card>
              
              <Card className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Transactions</p>
                    <p className="text-2xl font-bold" data-testid="total-transactions">
                      {stats?.summary?.total_transactions || 0}
                    </p>
                  </div>
                  <Receipt className="w-8 h-8 opacity-50" />
                </div>
              </Card>
              
              <Card className="p-4 bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Tickets Sold</p>
                    <p className="text-2xl font-bold" data-testid="tickets-sold">
                      {stats?.summary?.total_tickets_sold || 0}
                    </p>
                  </div>
                  <Ticket className="w-8 h-8 opacity-50" />
                </div>
              </Card>
              
              <Card className="p-4 bg-gradient-to-br from-orange-500 to-red-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80">Processors</p>
                    <p className="text-2xl font-bold" data-testid="connected-processors">
                      {stats?.summary?.connected_processors || 0}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 opacity-50" />
                </div>
              </Card>
            </div>
            
            {/* Connected Processors Preview */}
            {connections.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2 dark:text-white">
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                  Connected Processors
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {connections.map(conn => (
                    <div
                      key={conn.connection_id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                    >
                      <div className={`w-10 h-10 rounded-lg ${processorStyles[conn.processor]?.bgColor || 'bg-gray-500'} flex items-center justify-center text-white text-xl`}>
                        {processorStyles[conn.processor]?.icon || '💳'}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium dark:text-white">{conn.processor_name}</p>
                        <div className="flex items-center gap-2">
                          <Badge variant={conn.sandbox_mode ? 'secondary' : 'default'} className="text-xs">
                            {conn.sandbox_mode ? 'Sandbox' : 'Live'}
                          </Badge>
                          <span className="text-xs text-gray-500">{conn.fees}</span>
                        </div>
                      </div>
                      {conn.is_active && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card 
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 dark:border-gray-700"
                onClick={() => setShowTicketModal(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Ticket className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold dark:text-white">Create Tickets</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Sell tickets for your events
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card 
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-dashed border-gray-200 dark:border-gray-700"
                onClick={() => setShowConnectModal(true)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Plus className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold dark:text-white">Add Processor</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connect Stripe, PayPal, or Square
                    </p>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Recent Transactions */}
            {stats?.recent_transactions?.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2 dark:text-white">
                  <Clock className="w-5 h-5 text-blue-500" />
                  Recent Transactions
                </h3>
                <div className="space-y-3">
                  {stats.recent_transactions.map(txn => (
                    <div
                      key={txn.transaction_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-medium dark:text-white">{txn.ticket_name}</p>
                          <p className="text-sm text-gray-500">{txn.buyer_info?.name} • {txn.quantity}x</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">+{formatCurrency(txn.total_amount)}</p>
                        <p className="text-xs text-gray-500">{new Date(txn.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Empty State */}
            {connections.length === 0 && (
              <Card className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2 dark:text-white">No Payment Processors Connected</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Connect your payment processor to start accepting payments for events and reservations
                </p>
                <Button onClick={() => setShowConnectModal(true)} className="bg-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Connect Your First Processor
                </Button>
              </Card>
            )}
          </div>
        )}
        
        {/* Processors Tab */}
        {activeTab === 'processors' && (
          <div className="space-y-6">
            {/* Available Processors */}
            <div>
              <h3 className="font-semibold mb-4 dark:text-white">Available Payment Processors</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(processors).map(([key, processor]) => {
                  const isConnected = connections.some(c => c.processor === key);
                  const connection = connections.find(c => c.processor === key);
                  
                  return (
                    <Card 
                      key={key}
                      className={`p-5 transition-all ${isConnected ? 'ring-2 ring-green-500' : 'hover:shadow-lg'}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-2xl`}
                            style={{ backgroundColor: processor.color }}
                          >
                            {processorStyles[key]?.icon || '💳'}
                          </div>
                          <div>
                            <h4 className="font-semibold dark:text-white">{processor.name}</h4>
                            <p className="text-sm text-gray-500">{processor.fees}</p>
                          </div>
                        </div>
                        {isConnected && (
                          <Badge className="bg-green-500">Connected</Badge>
                        )}
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Globe className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600 dark:text-gray-400">
                            {processor.currencies?.slice(0, 3).join(', ')}{processor.currencies?.length > 3 ? '...' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600 dark:text-gray-400">{processor.payout_timing}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {processor.supports_subscriptions && (
                            <Badge variant="secondary" className="text-xs">Subscriptions</Badge>
                          )}
                          {processor.supports_refunds && (
                            <Badge variant="secondary" className="text-xs">Refunds</Badge>
                          )}
                        </div>
                      </div>
                      
                      {isConnected ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Mode:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{connection?.sandbox_mode ? 'Sandbox' : 'Live'}</span>
                              <Switch
                                checked={!connection?.sandbox_mode}
                                onCheckedChange={() => handleToggleSandbox(connection.connection_id, connection.sandbox_mode)}
                              />
                            </div>
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            className="w-full"
                            onClick={() => handleDisconnect(connection.connection_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Disconnect
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className="w-full"
                          onClick={() => {
                            setSelectedProcessor(key);
                            setShowConnectModal(true);
                          }}
                        >
                          Connect {processor.name}
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold dark:text-white">Your Event Tickets</h3>
              <Button onClick={() => setShowTicketModal(true)} disabled={connections.length === 0}>
                <Plus className="w-4 h-4 mr-2" />
                Create Ticket
              </Button>
            </div>
            
            {connections.length === 0 ? (
              <Card className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <h4 className="font-semibold mb-2 dark:text-white">Connect a Payment Processor First</h4>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  You need to connect a payment processor before creating tickets
                </p>
                <Button onClick={() => setActiveTab('processors')}>
                  Go to Processors
                </Button>
              </Card>
            ) : userLocales.length === 0 ? (
              <Card className="p-8 text-center">
                <Ticket className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="font-semibold mb-2 dark:text-white">No Locale Events</h4>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Create a Locale event first to start selling tickets
                </p>
                <Button onClick={() => navigate('/locale')}>
                  Go to Locale
                </Button>
              </Card>
            ) : (
              <Card className="p-6">
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  Click "Create Ticket" to add tickets to your events
                </p>
              </Card>
            )}
          </div>
        )}
        
        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold dark:text-white">Transaction History</h3>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {transactions.length > 0 ? (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Order</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Buyer</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Item</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {transactions.map(txn => (
                        <tr key={txn.transaction_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm dark:text-white">{txn.order_number}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium dark:text-white">{txn.buyer_info?.name}</p>
                              <p className="text-xs text-gray-500">{txn.buyer_info?.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm dark:text-gray-300">{txn.ticket_name} x{txn.quantity}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-green-600">{formatCurrency(txn.total_amount)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={txn.status === 'completed' ? 'default' : 'secondary'}>
                              {txn.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(txn.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="font-semibold mb-2 dark:text-white">No Transactions Yet</h4>
                <p className="text-gray-500 dark:text-gray-400">
                  Transactions will appear here when customers make purchases
                </p>
              </Card>
            )}
          </div>
        )}
      </div>
      
      {/* Connect Processor Modal */}
      <AnimatePresence>
        {showConnectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowConnectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 dark:text-white">
                Connect {selectedProcessor ? processors[selectedProcessor]?.name : 'Payment Processor'}
              </h3>
              
              {!selectedProcessor ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Select a payment processor to connect:
                  </p>
                  {Object.entries(processors).map(([key, processor]) => {
                    const isConnected = connections.some(c => c.processor === key);
                    if (isConnected) return null;
                    
                    return (
                      <button
                        key={key}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        onClick={() => setSelectedProcessor(key)}
                      >
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xl"
                          style={{ backgroundColor: processor.color }}
                        >
                          {processorStyles[key]?.icon || '💳'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium dark:text-white">{processor.name}</p>
                          <p className="text-sm text-gray-500">{processor.fees}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Account Email</label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={connectForm.account_email}
                      onChange={e => setConnectForm(prev => ({ ...prev, account_email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">API Key (optional)</label>
                    <Input
                      type="password"
                      placeholder="pk_live_..."
                      value={connectForm.api_key}
                      onChange={e => setConnectForm(prev => ({ ...prev, api_key: e.target.value }))}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank to use OAuth connection</p>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                    <div>
                      <p className="font-medium text-yellow-800 dark:text-yellow-200">Sandbox Mode</p>
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">Use test credentials</p>
                    </div>
                    <Switch
                      checked={connectForm.sandbox_mode}
                      onCheckedChange={checked => setConnectForm(prev => ({ ...prev, sandbox_mode: checked }))}
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedProcessor(null);
                        setConnectForm({ api_key: '', secret_key: '', merchant_id: '', account_email: '', sandbox_mode: true });
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      className="flex-1 bg-primary"
                      onClick={handleConnect}
                      disabled={connecting}
                    >
                      {connecting ? 'Connecting...' : 'Connect'}
                    </Button>
                  </div>
                </div>
              )}
              
              {!selectedProcessor && (
                <Button
                  variant="ghost"
                  className="w-full mt-4"
                  onClick={() => setShowConnectModal(false)}
                >
                  Cancel
                </Button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Create Ticket Modal */}
      <AnimatePresence>
        {showTicketModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowTicketModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4 dark:text-white flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-500" />
                Create Event Ticket
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Event *</label>
                  <select
                    value={ticketForm.locale_id}
                    onChange={e => setTicketForm(prev => ({ ...prev, locale_id: e.target.value }))}
                    className="w-full mt-1 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">Choose an event...</option>
                    {userLocales.map(locale => (
                      <option key={locale.locale_id} value={locale.locale_id}>
                        {locale.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ticket Name *</label>
                  <Input
                    placeholder="e.g., General Admission, VIP Pass"
                    value={ticketForm.ticket_name}
                    onChange={e => setTicketForm(prev => ({ ...prev, ticket_name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Price *</label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={ticketForm.price}
                        onChange={e => setTicketForm(prev => ({ ...prev, price: e.target.value }))}
                        className="pl-7"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Quantity</label>
                    <Input
                      type="number"
                      value={ticketForm.quantity_available}
                      onChange={e => setTicketForm(prev => ({ ...prev, quantity_available: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                  <Textarea
                    placeholder="What's included with this ticket..."
                    value={ticketForm.description}
                    onChange={e => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Per Order</label>
                  <Input
                    type="number"
                    value={ticketForm.max_per_order}
                    onChange={e => setTicketForm(prev => ({ ...prev, max_per_order: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowTicketModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={handleCreateTicket}
                  >
                    Create Ticket
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <FloatingDock user={user} />
    </div>
  );
};

export default LocalePayments;
